import { useCallback, useMemo } from "react";
import {
  clearLocalStorageForRegistration,
  setSession,
  setSessionUserRole,
  setUserLanguage,
  setUserId,
  setTempLanguage,
  getTempLanguage,
} from "../api/sessionManagement";
import { userLogin } from "../api/userRegistration";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { getCurrentLanguage } from "../i18n";

interface UseSignUpReturnValues {
  handleSignUp: (
    userPortalEmail: string,
    userName: string,
    email: string,
    password: string,
    region: string,
    companyName: string,
    vat: string,
    phone: string,
    registration: string,
    zip: string,
    city: string,
    address: string,
    owners: string[],
    boards: string[]
  ) => void;
}

const DEFAULT_LANGUAGE = "en-GB";

export const useSignUp = (): UseSignUpReturnValues => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();

  const optionsForAlertError: any = useMemo(() => ({
    title: t("oops"),
    text: t("registrationError"),
    icon: "error",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  }), [t]);

  const optionsForAlertWithUserConflict: any = useMemo(() => ({
    title: t("oops"),
    text: t("userConflict"),
    icon: "error",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  }), [t]);

  const handleSignUp = useCallback(
    async (
      userPortalEmail: string,
      userName: string,
      email: string,
      password: string,
      region: string,
      companyName: string,
      vat: string,
      phone: string,
      registration: string,
      zip: string,
      city: string,
      address: string,
      owners: string[],
      boards: string[]
    ) => {
      const lang = getCurrentLanguage();
      setTempLanguage(lang);
      clearLocalStorageForRegistration();
      const userSession = await userLogin(
        userPortalEmail?.trim() || "",
        userName,
        email?.trim() || "",
        password?.trim() || "",
        region,
        companyName,
        vat,
        phone,
        registration,
        zip,
        city,
        address,
        owners,
        boards,
        lang
      );
      const savedLang = getTempLanguage() || lang;
      localStorage.setItem("i18nextLng", savedLang);
      if (!userSession.error) {
        if (userSession.data) {
          const userId = userSession.data.data.user_id;
          const userLanguage =
            userSession.data.data?.lang?.replace("_", "-") ?? DEFAULT_LANGUAGE;

          setUserLanguage(userId, userLanguage);
          localStorage.setItem("i18nextLng", userLanguage);
          await i18n.changeLanguage(userLanguage);

          setUserId(userId);
          setSession(userSession.data.data.session);
          setSessionUserRole(userSession.data.data.role ?? "confirmed");
          localStorage.setItem("isSecondStep", JSON.stringify(true));
          const event = new Event("localStorageUpdate");
          window.dispatchEvent(event);
        }
      } else {
        if (userSession.error.response?.status === 409) {
          Swal.fire(optionsForAlertWithUserConflict).then(() => {
            localStorage.setItem("isSecondStep", JSON.stringify(false));
            const event = new Event("localStorageUpdate");
            window.dispatchEvent(event);
          });
        } else {
          Swal.fire(optionsForAlertError).then(() => {
            localStorage.setItem("isSecondStep", JSON.stringify(false));
            const event = new Event("localStorageUpdate");
            window.dispatchEvent(event);
          });
        }
      }
    },
    [i18n, optionsForAlertError, optionsForAlertWithUserConflict]
  );

  return { handleSignUp };
};
